import type { SessionProfile } from "../../shared/types/domain";
import { demoOrganizationRepository } from "../organization/demoOrganizationRepository";
import { demoAuditAdapter, demoNotificationAdapter } from "../governance/demoGovernanceRepository";
import type { EnergyClaim, IncentivesState } from "./incentives.types";
const KEY = "boom-v4-incentives",
  storeId = "20000000-0000-4000-8000-000000000001";
const seed: IncentivesState = {
  rules: [
    rule(
      "50000000-0000-4000-8000-000000000001",
      "POSITIVE_FEEDBACK",
      "会员高分评价",
      "获得真实会员95分及以上课程评价",
      50,
    ),
    rule(
      "50000000-0000-4000-8000-000000000002",
      "RENEWAL_SUPPORT",
      "协助会员续课",
      "完成续课服务并形成有效订单",
      100,
    ),
    rule(
      "50000000-0000-4000-8000-000000000003",
      "SERVICE_RECOVERY",
      "服务补救闭环",
      "妥善完成低评分或投诉闭环",
      80,
    ),
  ],
  claims: [],
  appeals: [],
  honors: [],
  clearings: [],
  ledger: [
    {
      id: "51000000-0000-4000-8000-000000000001",
      employeeId: "10000000-0000-4000-8000-000000000002",
      storeId,
      changePoints: 360,
      balanceAfter: 360,
      sourceType: "adjustment",
      sourceId: "52000000-0000-4000-8000-000000000001",
      description: "V4演示期初星能",
      occurredAt: "2026-07-01T00:00:00.000Z",
    },
  ],
};
function rule(
  id: string,
  code: string,
  name: string,
  description: string,
  points: number,
) {
  return {
    id,
    code,
    name,
    description,
    points,
    scope: "company" as const,
    storeId: null,
    active: true,
    createdBy: "10000000-0000-4000-8000-000000000004",
    createdAt: "2026-07-01T00:00:00.000Z",
  };
}
const clone = <T>(v: T): T => structuredClone(v);
function load() {
  const raw = localStorage.getItem(KEY);
  if (!raw) return clone(seed);
  try {
    const parsed = JSON.parse(raw) as IncentivesState;
    return { ...parsed, appeals: parsed.appeals ?? [], honors: parsed.honors ?? [], clearings: parsed.clearings ?? [] };
  } catch {
    return clone(seed);
  }
}
function save(s: IncentivesState) {
  localStorage.setItem(KEY, JSON.stringify(s));
}
function fail(m: string): never {
  throw new Error(m);
}
async function context(a: SessionProfile) {
  const org = await demoOrganizationRepository.getState();
  const account = org.accounts.find((x) => x.id === a.id);
  if (!account || account.status !== "active") fail("当前账号不可用。");
  return org;
}
function balance(s: IncentivesState, id: string) {
  return s.ledger
    .filter((x) => x.employeeId === id)
    .reduce((n, x) => n + x.changePoints, 0);
}
export const demoIncentivesRepository = {
  async getState() {
    return load();
  },
  async reset() {
    localStorage.removeItem(KEY);
    return clone(seed);
  },
  async createRule(
    actor: SessionProfile,
    input: {
      name: string;
      description: string;
      points: number;
      scope: "company" | "store";
      storeId: string | null;
    },
  ) {
    await context(actor);
    if (actor.role !== "super") fail("只有超管可以创建星能规则。");
    if (
      !input.name.trim() ||
      !input.description.trim() ||
      !Number.isInteger(input.points) ||
      input.points <= 0
    )
      fail("规则名称、说明和正整数星能值必填。");
    if (input.scope === "store" && !input.storeId)
      fail("门店规则必须选择门店。");
    const s = load(),
      next = {
        id: crypto.randomUUID(),
        code: `CUSTOM_${Date.now()}`,
        name: input.name.trim(),
        description: input.description.trim(),
        points: input.points,
        scope: input.scope,
        storeId: input.scope === "store" ? input.storeId : null,
        active: true,
        createdBy: actor.id,
        createdAt: new Date().toISOString(),
      };
    s.rules.unshift(next);
    save(s);
    return clone(next);
  },
  async submitClaim(
    actor: SessionProfile,
    input: { ruleId: string; evidence: string; occurredOn: string; file?: File | null },
  ) {
    await context(actor);
    if (actor.role !== "employee") fail("只有员工可以申报星能。");
    if (!actor.storeId) fail("员工必须绑定门店。");
    const s = load(),
      r =
        s.rules.find(
          (x) =>
            x.id === input.ruleId &&
            x.active &&
            (x.scope === "company" || x.storeId === actor.storeId),
        ) ?? fail("星能规则不可用。");
    if (input.evidence.trim().length < 4) fail("请填写可核验的成果说明。");
    const day = new Date(input.occurredOn);
    if (!Number.isFinite(day.getTime()) || day > new Date())
      fail("成果日期无效。");
    const next: EnergyClaim = {
      id: crypto.randomUUID(),
      ruleId: r.id,
      employeeId: actor.id,
      storeId: actor.storeId,
      evidence: input.evidence.trim(),
      evidencePath: input.file ? `demo/energy-evidence/${input.file.name}` : null,
      evidenceFileName: input.file?.name ?? "",
      evidenceMimeType: input.file?.type ?? "",
      evidenceFileSize: input.file?.size ?? null,
      evidencePreviewUrl: input.file ? URL.createObjectURL(input.file) : "",
      occurredOn: input.occurredOn,
      status: "pending",
      reviewerId: null,
      reviewReason: "",
      createdAt: new Date().toISOString(),
      reviewedAt: null,
    };
    s.claims.unshift(next);
    save(s);
    demoAuditAdapter.record({ actorId: actor.id, action: "energy_claim.submit", entityType: "energy_claim", entityId: next.id, reason: next.evidence });
    return clone(next);
  },
  async reviewClaim(
    actor: SessionProfile,
    input: { claimId: string; decision: "approve" | "reject"; reason: string },
  ) {
    await context(actor);
    if (!["manager", "super"].includes(actor.role))
      fail("只有店长或超管可以审批星能。");
    const s = load(),
      c =
        s.claims.find((x) => x.id === input.claimId) ??
        fail("星能申报不存在。");
    if (c.status !== "pending") fail("星能申报已经处理。");
    if (actor.id === c.employeeId) fail("申报人不能自审。");
    if (actor.role === "manager" && actor.storeId !== c.storeId)
      fail("不能审批其他门店申报。");
    if (input.decision === "reject" && !input.reason.trim())
      fail("驳回必须填写原因。");
    c.status = input.decision === "approve" ? "approved" : "rejected";
    c.reviewerId = actor.id;
    c.reviewReason = input.reason.trim();
    c.reviewedAt = new Date().toISOString();
    if (input.decision === "approve") {
      const r =
        s.rules.find((x) => x.id === c.ruleId) ?? fail("星能规则不存在。");
      const after = balance(s, c.employeeId) + r.points;
      s.ledger.unshift({
        id: crypto.randomUUID(),
        employeeId: c.employeeId,
        storeId: c.storeId,
        changePoints: r.points,
        balanceAfter: after,
        sourceType: "claim",
        sourceId: c.id,
        description: r.name,
        occurredAt: c.reviewedAt,
      });
    }
    save(s);
    demoAuditAdapter.record({ actorId: actor.id, action: `energy_claim.${input.decision}`, entityType: "energy_claim", entityId: c.id, reason: input.reason.trim() });
    demoNotificationAdapter.notify({
      recipientId: c.employeeId,
      storeId: c.storeId,
      type: "energy",
      title: input.decision === "approve" ? "星能申报已通过" : "星能申报已驳回",
      content: c.reviewReason || "查看申报详情。",
      entityType: "energy_claim",
      entityId: c.id,
      dedupeKey: `energy_claim:${c.id}:energy`,
    });
    return clone(c);
  },
  async submitAppeal(
    actor: SessionProfile,
    input: { claimId: string; reason: string },
  ) {
    await context(actor);
    const s = load();
    const claim =
      s.claims.find((item) => item.id === input.claimId) ??
      fail("星能申报不存在。");
    if (actor.role !== "employee" || claim.employeeId !== actor.id)
      fail("只能申诉自己的星能申报。");
    if (claim.status !== "rejected") fail("只有已驳回申报可以申诉。");
    if (input.reason.trim().length < 4) fail("请填写完整的申诉说明。");
    if (s.appeals.some((item) => item.claimId === claim.id))
      fail("该申报已经提交过申诉。");
    const appeal = {
      id: crypto.randomUUID(),
      claimId: claim.id,
      employeeId: actor.id,
      storeId: claim.storeId,
      reason: input.reason.trim(),
      status: "pending" as const,
      reviewerId: null,
      reviewReason: "",
      createdAt: new Date().toISOString(),
      reviewedAt: null,
    };
    s.appeals.unshift(appeal);
    save(s);
    demoAuditAdapter.record({ actorId: actor.id, action: "energy_appeal.submit", entityType: "energy_appeal", entityId: appeal.id, reason: appeal.reason });
    return clone(appeal);
  },
  async reviewAppeal(
    actor: SessionProfile,
    input: { appealId: string; decision: "approve" | "reject"; reason: string },
  ) {
    await context(actor);
    if (!["manager", "super"].includes(actor.role))
      fail("只有店长或超管可以复核申诉。");
    const s = load();
    const appeal =
      s.appeals.find((item) => item.id === input.appealId) ??
      fail("申诉不存在。");
    if (appeal.status !== "pending") fail("申诉已经处理。");
    if (appeal.employeeId === actor.id) fail("申诉人不能自审。");
    if (actor.role === "manager" && actor.storeId !== appeal.storeId)
      fail("不能复核其他门店申诉。");
    if (input.decision === "reject" && input.reason.trim().length < 2)
      fail("维持驳回必须填写原因。");
    appeal.status = input.decision === "approve" ? "approved" : "rejected";
    appeal.reviewerId = actor.id;
    appeal.reviewReason = input.reason.trim();
    appeal.reviewedAt = new Date().toISOString();
    if (input.decision === "approve") {
      const claim =
        s.claims.find((item) => item.id === appeal.claimId) ??
        fail("原申报不存在。");
      const rule =
        s.rules.find((item) => item.id === claim.ruleId) ??
        fail("星能规则不存在。");
      if (
        s.ledger.some(
          (item) => item.sourceType === "claim" && item.sourceId === claim.id,
        )
      )
        fail("原申报已经入账。");
      s.ledger.unshift({
        id: crypto.randomUUID(),
        employeeId: appeal.employeeId,
        storeId: appeal.storeId,
        changePoints: rule.points,
        balanceAfter: balance(s, appeal.employeeId) + rule.points,
        sourceType: "claim",
        sourceId: claim.id,
        description: `${rule.name}（申诉补发）`,
        occurredAt: appeal.reviewedAt,
      });
    }
    save(s);
    demoAuditAdapter.record({ actorId: actor.id, action: `energy_appeal.${input.decision}`, entityType: "energy_appeal", entityId: appeal.id, reason: input.reason.trim() });
    demoNotificationAdapter.notify({
      recipientId: appeal.employeeId,
      storeId: appeal.storeId,
      type: "energy",
      title:
        input.decision === "approve" ? "星能申诉已通过" : "星能申诉维持驳回",
      content: appeal.reviewReason || "查看申诉详情。",
      entityType: "energy_appeal",
      entityId: appeal.id,
      dedupeKey: `energy_appeal:${appeal.id}:energy`,
    });
    return clone(appeal);
  },
  async adjust(
    actor: SessionProfile,
    input: { employeeId: string; points: number; reason: string },
  ) {
    const org = await context(actor);
    if (actor.role !== "super") fail("只有超管可以调整星能。");
    if (
      !Number.isInteger(input.points) ||
      input.points === 0 ||
      !input.reason.trim()
    )
      fail("调整值和原因不能为空。");
    const employee =
      org.accounts.find(
        (x) =>
          x.id === input.employeeId &&
          x.role === "employee" &&
          x.status === "active",
      ) ?? fail("员工不可用。");
    if (!employee.storeId) fail("员工未绑定门店。");
    const s = load(),
      after = balance(s, employee.id) + input.points;
    if (after < 0) fail("调整后余额不能为负数。");
    const item = {
      id: crypto.randomUUID(),
      employeeId: employee.id,
      storeId: employee.storeId,
      changePoints: input.points,
      balanceAfter: after,
      sourceType: "adjustment" as const,
      sourceId: crypto.randomUUID(),
      description: input.reason.trim(),
      occurredAt: new Date().toISOString(),
    };
    s.ledger.unshift(item);
    save(s);
    demoAuditAdapter.record({ actorId: actor.id, action: "energy.adjust", entityType: "energy_ledger", entityId: item.id, reason: item.description });
    return clone(item);
  },
  async awardHonor(actor: SessionProfile, input: { employeeId: string; title: string; description: string; badge: string; points: number }) {
    const org = await context(actor);
    if (actor.role !== "super") fail("只有超管可以授予员工荣誉。");
    const employee = org.accounts.find((item) => item.id === input.employeeId && item.role === "employee" && item.status === "active") ?? fail("员工不可用。");
    if (!employee.storeId || input.title.trim().length < 2 || input.description.trim().length < 2 || !Number.isInteger(input.points) || input.points < 0) fail("请完整填写荣誉信息，奖励星能不能为负数。");
    const s = load();
    const honor = { id: crypto.randomUUID(), employeeId: employee.id, storeId: employee.storeId, title: input.title.trim(), description: input.description.trim(), badge: input.badge.trim() || "🏆", points: input.points, awardedBy: actor.id, awardedAt: new Date().toISOString() };
    s.honors.unshift(honor);
    if (input.points > 0) s.ledger.unshift({ id: crypto.randomUUID(), employeeId: employee.id, storeId: employee.storeId, changePoints: input.points, balanceAfter: balance(s, employee.id) + input.points, sourceType: "honor", sourceId: honor.id, description: `荣誉奖励：${honor.title}`, occurredAt: honor.awardedAt });
    save(s);
    demoAuditAdapter.record({ actorId: actor.id, action: "employee_honor.award", entityType: "employee_honor", entityId: honor.id, reason: honor.title });
    return clone(honor);
  },
  async runAnnualClearing(actor: SessionProfile, input: { year: number; carryoverLimit: number }) {
    const org = await context(actor);
    if (actor.role !== "super") fail("只有超管可以执行年度清算。");
    if (!Number.isInteger(input.year) || input.year < 2020 || input.year > new Date().getFullYear() || !Number.isInteger(input.carryoverLimit) || input.carryoverLimit < 0) fail("清算年份或结转上限无效。");
    const s = load();
    if (s.clearings.some((item) => item.year === input.year)) fail("该年度已经完成清算。");
    const clearingId = crypto.randomUUID(); let employeeCount = 0; let clearedPoints = 0;
    for (const employee of org.accounts.filter((item) => item.role === "employee" && item.storeId)) {
      const current = balance(s, employee.id); const deduction = Math.max(current - input.carryoverLimit, 0);
      if (!deduction) continue;
      employeeCount += 1; clearedPoints += deduction;
      s.ledger.unshift({ id: crypto.randomUUID(), employeeId: employee.id, storeId: employee.storeId!, changePoints: -deduction, balanceAfter: current - deduction, sourceType: "clearing", sourceId: crypto.randomUUID(), description: `${input.year}年度星能清算`, occurredAt: new Date().toISOString() });
    }
    const result = { id: clearingId, year: input.year, carryoverLimit: input.carryoverLimit, employeeCount, clearedPoints, executedBy: actor.id, executedAt: new Date().toISOString() };
    s.clearings.unshift(result); save(s); demoAuditAdapter.record({ actorId: actor.id, action: "energy.annual_clearing", entityType: "energy_clearing", entityId: result.id, reason: `${result.year}年度清算${result.clearedPoints}星能` }); return clone(result);
  },
  balanceOf(employeeId: string) {
    return balance(load(), employeeId);
  },
  ranking() {
    const s = load(),
      ids = [...new Set(s.ledger.map((x) => x.employeeId))];
    return ids
      .map((employeeId) => ({ employeeId, points: balance(s, employeeId) }))
      .sort((a, b) => b.points - a.points);
  },
};

export const demoEnergyLedgerAdapter = {
  balanceOf(employeeId: string) {
    return balance(load(), employeeId);
  },
  spend(
    employeeId: string,
    storeId: string,
    points: number,
    sourceId: string,
    description: string,
  ) {
    const s = load();
    if (
      s.ledger.some(
        (x) => x.sourceType === "redemption" && x.sourceId === sourceId,
      )
    )
      return;
    const after = balance(s, employeeId) - points;
    if (after < 0) fail("星能余额不足。");
    s.ledger.unshift({
      id: crypto.randomUUID(),
      employeeId,
      storeId,
      changePoints: -points,
      balanceAfter: after,
      sourceType: "redemption",
      sourceId,
      description,
      occurredAt: new Date().toISOString(),
    });
    save(s);
  },
  awardAutomatic(
    employeeId: string,
    storeId: string,
    ruleCode: string,
    eventId: string,
  ) {
    const s = load();
    if (
      s.ledger.some(
        (item) => item.sourceType === "adjustment" && item.sourceId === eventId,
      )
    )
      return;
    const rule = s.rules.find(
      (item) =>
        item.code === ruleCode &&
        item.active &&
        (item.scope === "company" || item.storeId === storeId),
    );
    if (!rule) return;
    const after = balance(s, employeeId) + rule.points;
    s.ledger.unshift({
      id: crypto.randomUUID(),
      employeeId,
      storeId,
      changePoints: rule.points,
      balanceAfter: after,
      sourceType: "adjustment",
      sourceId: eventId,
      description: `${rule.name}（业务自动奖励）`,
      occurredAt: new Date().toISOString(),
    });
    save(s);
  },
};
